import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import SearchableSelect from 'components/SearchableSelect'
import FileTreeSelector from 'features/bpDetails/Components/files/FileTreeSelector'
import APIService from 'services/APIService'
import { ROLE_OPTIONS, DOMAIN_ROLE_OPTIONS } from 'utils/roleOptions'
import { lookupUserByEmail } from 'services/accessService'
import { ink, surface, line, radius, font } from 'features/dashboard/tokens'
import RolesExplainer from 'features/organisation/RolesExplainer'

const filesApi = APIService()

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim())

const normalizeSummaryCategory = (category) => ({
  ...category,
  fileTypes: category.fileTypes || category.file_types,
  file_types: category.fileTypes || category.file_types,
  is_loaded: false
})

const getEditableFileIds = (categories) => {
  const ids = []
  categories.forEach((category) => {
    Object.values(category.fileTypes || category.file_types || {}).forEach((fileType) => {
      fileType.files?.forEach((file) => {
        if ((file?.is_access_editable ?? file?.isAccessEditable ?? true) === true) {
          ids.push(file.id)
        }
      })
    })
  })
  return ids
}

const AccessAddModal = ({ scopeType, scopeId, api, scopeName, initialSubject, onClose, onDone }) => {
  const isContainer = scopeType !== 'building'
  const [subject, setSubject] = useState(initialSubject === 'domain' && scopeType !== 'building' ? 'domain' : 'person')
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [existingUser, setExistingUser] = useState(null)
  const [checkingEmail, setCheckingEmail] = useState(false)
  const [domain, setDomain] = useState('')
  const [relationType, setRelationType] = useState('agent')
  const [fileAccess, setFileAccess] = useState('none')
  const [fileIds, setFileIds] = useState([])
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const [fileTree, setFileTree] = useState([])
  const [fileTreeLoading, setFileTreeLoading] = useState(false)
  const [loadingCategoryIds, setLoadingCategoryIds] = useState([])
  const [selectAllLoading, setSelectAllLoading] = useState(false)

  const mergeCategoryIntoTree = useCallback((incomingCategory) => {
    setFileTree((prev) => {
      const nextTree = [...prev]
      const existingIndex = nextTree.findIndex((category) => category.id === incomingCategory.id)
      const existingCategory = existingIndex >= 0 ? nextTree[existingIndex] : null

      if (existingIndex >= 0) {
        nextTree[existingIndex] = {
          ...existingCategory,
          ...incomingCategory,
          fileTypes: incomingCategory.fileTypes || incomingCategory.file_types,
          file_types: incomingCategory.fileTypes || incomingCategory.file_types,
          is_loaded: true
        }
      } else {
        nextTree.push({
          ...incomingCategory,
          fileTypes: incomingCategory.fileTypes || incomingCategory.file_types,
          file_types: incomingCategory.fileTypes || incomingCategory.file_types,
          is_loaded: true
        })
      }

      return nextTree
    })
  }, [])

  const fetchFileTreeSummary = useCallback(async () => {
    setFileTreeLoading(true)

    try {
      const res = await filesApi.get(`/building-passport/${scopeId}/files-tree?include_files=0`)
      const data = res?.data?.data || res?.data || []
      const normalizedData = (Array.isArray(data) ? data : Object.values(data)).map(normalizeSummaryCategory)
      setFileTree(normalizedData)
    } catch (err) {
      console.error('Failed to fetch file tree:', err)
    } finally {
      setFileTreeLoading(false)
    }
  }, [scopeId])

  const fetchCategoryFiles = useCallback(async (categoryId) => {
    const targetCategory = fileTree.find((category) => category.id === categoryId)
    if (!targetCategory || targetCategory.is_loaded || loadingCategoryIds.includes(categoryId)) {
      return targetCategory
    }

    setLoadingCategoryIds((prev) => [...prev, categoryId])

    try {
      const res = await filesApi.get(`/building-passport/${scopeId}/files-tree?category_id=${categoryId}`)
      const data = res?.data?.data || res?.data || []
      const category = Array.isArray(data) ? data[0] : Object.values(data)[0]

      if (category) {
        mergeCategoryIntoTree(category)
      }
      return category || targetCategory
    } catch (err) {
      console.error('Failed to fetch category files:', err)
      return targetCategory
    } finally {
      setLoadingCategoryIds((prev) => prev.filter((id) => id !== categoryId))
    }
  }, [scopeId, fileTree, loadingCategoryIds, mergeCategoryIntoTree])

  const fetchCategoryFilesBatch = useCallback(async (categoryIds) => {
    const targetCategoryIds = categoryIds.filter((categoryId) => {
      const category = fileTree.find((item) => item.id === categoryId)
      return category && category.is_loaded !== true && !loadingCategoryIds.includes(categoryId)
    })

    if (targetCategoryIds.length === 0) {
      return fileTree.filter((category) => categoryIds.includes(category.id))
    }

    setLoadingCategoryIds((prev) => Array.from(new Set([...prev, ...targetCategoryIds])))

    try {
      const res = await filesApi.get(`/building-passport/${scopeId}/files-tree?category_ids=${targetCategoryIds.join(',')}`)
      const data = res?.data?.data || []
      const categories = Array.isArray(data) ? data : Object.values(data)

      categories.forEach((category) => {
        mergeCategoryIntoTree(category)
      })

      return categories
    } catch (err) {
      console.error('Failed to batch fetch category files:', err)
      return []
    } finally {
      setLoadingCategoryIds((prev) => prev.filter((id) => !targetCategoryIds.includes(id)))
    }
  }, [scopeId, fileTree, loadingCategoryIds, mergeCategoryIntoTree])

  const loadedEditableFileIds = useMemo(
    () => getEditableFileIds(fileTree.filter((category) => category.is_loaded !== false)),
    [fileTree]
  )

  const allFilesSelected = (
    loadedEditableFileIds.length > 0 &&
    fileTree.every((category) => category.is_loaded !== false) &&
    loadedEditableFileIds.every((fileId) => fileIds.includes(fileId))
  )

  const handleSelectDeselectAllFiles = useCallback(async () => {
    if (allFilesSelected) {
      setFileIds([])
      return
    }

    setSelectAllLoading(true)

    try {
      const unloadedCategoryIds = fileTree.filter((category) => category.is_loaded === false).map((category) => category.id)
      const loadedCategories = fileTree.filter((category) => category.is_loaded !== false)
      const fetchedCategories = await fetchCategoryFilesBatch(unloadedCategoryIds)
      const categoriesById = new Map()
      const allLoadedCategories = [...loadedCategories, ...fetchedCategories]

      allLoadedCategories.forEach((category) => {
        categoriesById.set(category.id, category)
      })

      setFileIds(Array.from(new Set(getEditableFileIds(Array.from(categoriesById.values())))))
    } finally {
      setSelectAllLoading(false)
    }
  }, [allFilesSelected, fetchCategoryFilesBatch, fileTree])

  useEffect(() => {
    if (!isContainer && scopeId) fetchFileTreeSummary()
  }, [isContainer, scopeId, fetchFileTreeSummary])

  // Check whether the typed email already belongs to a platform account, so we
  // can link an existing person (no name needed) or collect a name for a new invite.
  useEffect(() => {
    if (subject !== 'person') { setExistingUser(null); setCheckingEmail(false); return }
    const clean = email.trim().toLowerCase()
    if (!isValidEmail(clean)) { setExistingUser(null); setCheckingEmail(false); return }
    let cancelled = false
    setCheckingEmail(true)
    const timer = setTimeout(async () => {
      const user = await lookupUserByEmail(clean)
      if (cancelled) return
      setExistingUser(user)
      setCheckingEmail(false)
      if (user) {
        setFirstName(user.firstName || '')
        setLastName(user.lastName || '')
      }
    }, 400)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [email, subject])

  useEffect(() => {
    if (subject !== 'domain' || !isContainer) { setPreview(null); return }
    const clean = domain.trim().toLowerCase()
    if (!/^[^\s@]+\.[^\s@]+$/.test(clean)) { setPreview(null); return }
    let cancelled = false
    setPreviewLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res = await api.previewDomainGrant({ domain: clean, relationType })
        if (!cancelled) setPreview(res?.data?.data || null)
      } catch {
        if (!cancelled) setPreview(null)
      } finally {
        if (!cancelled) setPreviewLoading(false)
      }
    }, 450)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [subject, isContainer, domain, relationType, api])

  const submit = async () => {
    setBusy(true)
    try {
      if (isContainer && subject === 'domain') {
        if (!domain.trim()) { toast.error('Enter a domain.'); setBusy(false); return }
        await api.addDomainGrant({ domain: domain.trim().toLowerCase(), relationType })
        toast.success(`Everyone at ${domain.trim()} added to ${scopeName}.`)
      } else {
        if (!isValidEmail(email)) { toast.error('Enter a valid email.'); setBusy(false); return }
        if (checkingEmail) { toast.info('Just checking that email, one moment.'); setBusy(false); return }
        if (!existingUser && !firstName.trim()) { toast.error("Add the person's name so their invitation isn't blank."); setBusy(false); return }
        const body = { email: email.trim().toLowerCase(), relationType, expiresAt: null }
        if (existingUser?.id) {
          body.userId = existingUser.id
        } else {
          body.firstName = firstName.trim()
          body.lastName = lastName.trim()
        }
        if (!isContainer && fileAccess === 'all-file' && fileIds.length) body.filePermissions = fileIds
        const res = await api.assignUser(body)
        const status = res?.data?.data?.status ?? res?.data?.status
        const who = res?.data?.data?.name || res?.data?.name
          || [firstName.trim(), lastName.trim()].filter(Boolean).join(' ')
          || email.trim()
        if (status === 'invited' || (!existingUser && status !== 'assigned')) {
          toast.success(`Invitation sent to ${who}. They'll get an email to join ${scopeName}.`)
        } else {
          toast.success(`${who} added to ${scopeName}.`)
        }
      }
      onDone && onDone()
      onClose && onClose()
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not update access.')
    } finally { setBusy(false) }
  }

  const coveredBy = isContainer && subject === 'domain' && !previewLoading ? preview?.coveredBy || null : null

  const roleOpts = (isContainer && subject === 'domain' ? DOMAIN_ROLE_OPTIONS : ROLE_OPTIONS).map(({ value, label }) => ({ value, label }))
  const roleLabelText = roleOpts.find((o) => o.value === relationType)?.label || relationType

  return createPortal(
    <Backdrop onClick={onClose}><Card onClick={(e) => e.stopPropagation()}>
      <Title>Add to {scopeName}</Title>
      <Body>
        {api?.supportsDomainGrants && (
          <Toggle>
            <TBtn type='button' $on={subject === 'person'} onClick={() => setSubject('person')}>A person</TBtn>
            <TBtn type='button' data-tour-id='accessAdd.domain-toggle' $on={subject === 'domain'} onClick={() => setSubject('domain')}>A whole domain</TBtn>
          </Toggle>
        )}
        {subject === 'domain' ? (
          <>
            <Field><Label>Email domain</Label><Input data-tour-id='accessAdd.domain-field' placeholder='acme.com' value={domain} onChange={(e) => setDomain(e.target.value)} autoFocus /></Field>
            <Notice>Everyone in your organisation with an email at this domain gets this role automatically, now and in the future. To give one person a different role, add them as a person instead.</Notice>
          </>
        ) : (
          <Field><Label>User email</Label><Input data-tour-id='accessAdd.email' type='email' placeholder='name@example.com' value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></Field>
        )}
        {subject === 'person' && isValidEmail(email.trim()) && (
          <EmailStatus $existing={!!existingUser} $checking={checkingEmail}>
            {checkingEmail
              ? 'Checking if this person already has an account…'
              : existingUser
                ? `✓ ${[existingUser.firstName, existingUser.lastName].filter(Boolean).join(' ') || 'This account'} already exists, so we'll link them.`
                : "No account yet, so we'll email them an invitation. Add their name so it isn't blank."}
          </EmailStatus>
        )}
        {subject === 'person' && isValidEmail(email.trim()) && !checkingEmail && !existingUser && (
          <NameRow>
            <Field><Label>First name</Label><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder='Jane' /></Field>
            <Field><Label>Last name</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder='Smith' /></Field>
          </NameRow>
        )}
        <Field><LabelRow><Label>Role</Label><RolesExplainer /></LabelRow><div data-tour-id='accessAdd.role'><SearchableSelect ariaLabel='Role' value={relationType} options={roleOpts} onChange={setRelationType} /></div></Field>

        {isContainer && subject === 'domain' && (previewLoading || preview) && (
          <PreviewBox>
            {previewLoading ? (
              <PreviewMuted>Checking who this affects…</PreviewMuted>
            ) : coveredBy ? (
              <Warn role='status'>{coveredBy.message}</Warn>
            ) : preview.memberCount === 0 ? (
              <PreviewMuted>
                No one with an active account matches this domain yet. Anyone who joins later gets {roleLabelText} access automatically.
                {preview.alsoCovers?.length > 0 && ` This also covers ${preview.alsoCovers.map((d) => `@${d}`).join(', ')}, part of ${preview.organisationName || 'the same organisation'}.`}
              </PreviewMuted>
            ) : (
              <>
                <PreviewLine><b>{preview.memberCount}</b> {preview.memberCount === 1 ? 'person' : 'people'} with an account will get <b>{roleLabelText}</b> access to every building in this {scopeType}.</PreviewLine>
                {preview.alsoCovers?.length > 0 && (
                  <PreviewLine>
                    This counts everyone at {preview.alsoCovers.map((d) => `@${d}`).join(', ')} too, because {preview.alsoCovers.length === 1 ? 'it belongs' : 'they belong'} to {preview.organisationName || 'the same organisation'}.
                  </PreviewLine>
                )}
                {preview.alreadyHaveAccess > 0 && (
                  <PreviewLine $warn>
                    {preview.alreadyHaveAccess} of them already {preview.alreadyHaveAccess === 1 ? 'has' : 'have'} building access.{' '}
                    {preview.roleWillChange > 0
                      ? `${preview.roleWillChange} will be raised to ${roleLabelText} where their current role is lower.`
                      : 'None will be downgraded; this rule only raises a role where it is lower.'}
                  </PreviewLine>
                )}
              </>
            )}
          </PreviewBox>
        )}

        {!isContainer && subject === 'person' && (
          <Field>
            <Label>File access</Label>
            <Radio type='button' $on={fileAccess === 'none'} onClick={() => setFileAccess('none')}>No files</Radio>
            <Radio type='button' $on={fileAccess === 'all-file'} onClick={() => setFileAccess('all-file')}>Choose files…</Radio>
            {fileAccess === 'all-file' && (
              <FileTreeBox>
                <FileTreeActions>
                  <SelectAllBtn
                    type='button'
                    disabled={fileTreeLoading || selectAllLoading || fileTree.length === 0}
                    onClick={handleSelectDeselectAllFiles}
                  >
                    {selectAllLoading ? 'Selecting…' : (allFilesSelected ? 'Deselect all' : 'Select all')}
                  </SelectAllBtn>
                </FileTreeActions>
                <FileTreeSelector
                  fileTree={fileTree}
                  selected={fileIds}
                  onSelectionChange={setFileIds}
                  loading={fileTreeLoading}
                  loadingCategoryIds={loadingCategoryIds}
                  onCategoryExpand={fetchCategoryFiles}
                  maxHeight='220px'
                  compact
                />
              </FileTreeBox>
            )}
          </Field>
        )}
        {isContainer && subject === 'person' && (
          <Notice>Further file access will need to be controlled at a building-by-building level.</Notice>
        )}
        {isContainer && (
          <Warn>This grants access across every building in this {scopeType}.</Warn>
        )}
      </Body>

      <Footer>
        <Cancel type='button' onClick={onClose} disabled={busy}>Cancel</Cancel>
        <Primary type='button' data-tour-id='accessAdd.submit' onClick={submit} disabled={busy || !!coveredBy}>{busy ? 'Saving…' : 'Add'}</Primary>
      </Footer>
    </Card></Backdrop>,
    document.body
  )
}

export default AccessAddModal

const WARN_BG = '#fef3c7'
const WARN_FG = '#92400e'
const RADIO_SELECTED_BG = '#f0fbfc'

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 100050;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center; padding: 20px;
`
const Card = styled.div`
  background: ${surface.card}; border-radius: 14px; width: 100%; max-width: 460px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25); display: flex; flex-direction: column;
  max-height: calc(100vh - 40px); overflow: hidden;
`
const Title = styled.h2` margin: 0; padding: 20px 20px 4px; font-size: 17px; font-weight: 700; color: ${ink.base}; `
const Body = styled.div` padding: 16px 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; flex: 1; min-height: 0; `
const Toggle = styled.div`
  display: flex; gap: 4px; padding: 3px; background: ${surface.wash}; border-radius: 10px;
`
const TBtn = styled.button`
  flex: 1; padding: 8px 12px; border: none; border-radius: ${radius.sm}; cursor: pointer;
  font-size: ${font.body}; font-weight: 700;
  background: ${({ $on }) => ($on ? surface.card : 'transparent')};
  color: ${({ $on }) => ($on ? ink.base : ink.mute)};
  box-shadow: ${({ $on }) => ($on ? '0 1px 3px rgba(0,0,0,0.1)' : 'none')};
`
const Field = styled.div` display: flex; flex-direction: column; gap: 6px; `
const LabelRow = styled.div` display: flex; align-items: center; gap: 2px; `
const Label = styled.label` font-size: ${font.small}; font-weight: 700; color: ${ink.soft}; text-transform: uppercase; letter-spacing: 0.03em; `
const Input = styled.input`
  border: 1px solid ${line.strong}; border-radius: ${radius.sm}; padding: 10px 12px; font-size: 14px; outline: none;
  color: ${ink.base};
  &:focus { border-color: ${({ theme }) => theme.colors.secondary}; }
`
const EmailStatus = styled.div`
  margin-top: -8px;
  font-size: 12.5px;
  line-height: 1.45;
  font-weight: 600;
  color: ${({ $existing, $checking }) => ($existing ? '#15803d' : $checking ? ink.mute : ink.soft)};
`
const NameRow = styled.div`
  display: flex;
  gap: 10px;
  & > * { flex: 1; min-width: 0; }
`
const Radio = styled.button`
  display: flex; align-items: center; gap: 8px; width: 100%; text-align: left;
  padding: 10px 12px; border-radius: 9px; cursor: pointer;
  border: 1px solid ${({ $on, theme }) => ($on ? theme.colors.secondary : line.strong)};
  background: ${({ $on }) => ($on ? RADIO_SELECTED_BG : surface.card)};
  color: ${ink.base}; font-size: 13.5px; font-weight: 600;
  & + & { margin-top: 8px; }

  &::before {
    content: ''; flex-shrink: 0; width: 16px; height: 16px; border-radius: ${radius.round};
    border: 2px solid ${({ $on, theme }) => ($on ? theme.colors.secondary : line.strong)};
    box-shadow: ${({ $on, theme }) => ($on ? 'inset 0 0 0 3px ' + theme.colors.secondary : 'none')};
  }
`
const FileTreeBox = styled.div` margin-top: 2px; display: flex; flex-direction: column; gap: 6px; `
const FileTreeActions = styled.div` display: flex; justify-content: flex-end; `
const SelectAllBtn = styled.button`
  padding: 0; border: none; background: transparent; color: ${({ theme }) => theme.colors.secondary}; font-size: ${font.small}; font-weight: 700; cursor: pointer;
  &:hover:not(:disabled) { text-decoration: underline; }
  &:disabled { color: ${ink.mute}; cursor: default; }
`
const Notice = styled.div`
  padding: 10px 12px; border-radius: 9px;
  background: ${surface.wash}; color: ${ink.soft}; font-size: 12.5px; line-height: 1.5;
`
const Warn = styled.div`
  padding: 10px 12px; border-radius: 9px;
  background: ${WARN_BG}; color: ${WARN_FG}; font-size: ${font.body}; line-height: 1.5;
`
const PreviewBox = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  padding: 10px 12px; border-radius: 9px; border: 1px solid ${line.base}; background: ${surface.wash};
`
const PreviewMuted = styled.div` font-size: 12.5px; color: ${ink.mute}; line-height: 1.5; `
const PreviewLine = styled.div`
  font-size: 12.5px; line-height: 1.5;
  color: ${({ $warn }) => ($warn ? WARN_FG : ink.soft)};
  b { color: ${({ $warn }) => ($warn ? WARN_FG : ink.base)}; font-weight: 700; }
`
const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid ${line.base};
`
const Cancel = styled.button`
  padding: 9px 16px; border: 1px solid ${line.strong}; background: ${surface.card}; border-radius: ${radius.sm}; cursor: pointer;
  font-size: ${font.body}; font-weight: 600; color: ${ink.soft};
  &:disabled { opacity: 0.6; cursor: default; }
`
const Primary = styled.button`
  padding: 9px 18px; border: none; border-radius: ${radius.sm}; cursor: pointer; font-size: ${font.body}; font-weight: 700; color: #fff;
  background: ${({ theme }) => theme.colors.secondary};
  &:hover:not(:disabled) { filter: brightness(1.05); }
  &:disabled { opacity: 0.6; cursor: default; }
`
